import React from 'react';
import cn from 'classnames';
import { connect, ConnectedProps } from 'react-redux';
import { Location } from 'history';

import { OnDemandSizingCopy } from 'types/product';
import { isAssigned } from 'actions/ab';
import { HYDRA_PRODUCT_SIZING_PREDICTION } from 'constants/hydraTests';
import useMartyContext from 'hooks/useMartyContext';
import { selectPredictedSize } from 'selectors/product';
import RecommendedSizeTooltip from 'components/productdetail/stylepicker/RecommendedSizeTooltip';
import { RecommendedSizeFinderModal } from 'components/productdetail/asyncProductPageModals';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import { AppState } from 'types/app';
import { ProductDetailState } from 'reducers/detail/productDetail';

import css from 'styles/components/productdetail/recommendedSizing.scss';

interface LinkRecommendedSizingProps {
  predictedSize: string;
  selectedSize?: string;
  onOpenModal: () => void;
  isDesktopView: boolean;
  onDemandSizingCopy?: OnDemandSizingCopy;
  isHydraSizePrediction?: boolean;
}

export const LinkStyleRecommendedSizing = ({
  predictedSize,
  selectedSize,
  onOpenModal,
  isDesktopView,
  onDemandSizingCopy,
  isHydraSizePrediction = false
}: LinkRecommendedSizingProps) => {
  const { testId } = useMartyContext();

  const bothSizesSelected = predictedSize && selectedSize;
  const tooltipId = 'recommendedSizeTooltip';

  if (!onDemandSizingCopy) {
    return null;
  }

  const {
    ignoredcopy,
    ignoredimage,
    selectedcopy,
    selectedimage,
    availablecopy,
    availableimage,
    ignoredComponent = undefined,
    selectedComponent = undefined
  } = onDemandSizingCopy;

  const classNames = cn([css.linkStyleCopy, { [css.updatedSizingPrediction]: isHydraSizePrediction }]);

  if (bothSizesSelected && selectedSize !== predictedSize && ignoredcopy) {
    if (ignoredComponent) {
      return <div>{ignoredComponent}</div>;
    }

    return (
      <p style={ignoredimage ? { backgroundImage: `url('${ignoredimage}')` } : {}} className={classNames}>
        {ignoredcopy.replace(/%s/g, predictedSize)}
      </p>
    );
  } else if (bothSizesSelected && selectedSize === predictedSize && selectedcopy) {
    if (selectedComponent) {
      return <div>{selectedComponent}</div>;
    }

    return (
      <p style={selectedimage ? { backgroundImage: `url('${selectedimage}')` } : {}} className={classNames}>
        {selectedcopy}
      </p>
    );
  } else if (availablecopy) {
    return (
      <>
        {isDesktopView === true && <RecommendedSizeTooltip id={tooltipId} openModal={onOpenModal} />}
        <button
          style={availableimage ? { backgroundImage: `url('${availableimage}')` } : {}}
          className={classNames}
          type="button"
          aria-describedby={tooltipId}
          data-test-id={testId('initiateSizePrediction')}
          onClick={onOpenModal}
        >
          {availablecopy}
        </button>
      </>
    );
  } else {
    return null;
  }
};

interface RecommendedSizingOwnProps {
  gender: string | null;
  handleCalculateSizeClick: () => void;
  handleSetRecommendedFit: (predictedSize: string) => void;
  isOnDemandSizingModalOpen: boolean;
  onOpenModal: () => void;
  onCloseModal: () => void;
  product: ProductDetailState;
  selectedSize?: string;
  location: Location;
  isDesktopView: boolean;
}
type PropsFromRedux = ConnectedProps<typeof connector>;
type RecommendedSizingProps = RecommendedSizingOwnProps & PropsFromRedux;

export const RecommendedSizing = (props: RecommendedSizingProps) => {
  const {
    predictedSize,
    selectedSize,
    onOpenModal,
    onCloseModal,
    handleSetRecommendedFit,
    gender,
    product: { detail },
    handleCalculateSizeClick,
    isOnDemandSizingModalOpen,
    location,
    isDesktopView,
    onDemandSizingCopy,
    isHydraSizePrediction
  } = props;

  const { testId } = useMartyContext();

  if (!detail) {
    return null;
  }

  const { productId } = detail;

  const RecommendedSizeCopy = () => {
    const classNames = cn([css.linkStyleCopy, { [css.updatedSizingPrediction]: isHydraSizePrediction }]);

    return (
      <div className={css.recommendedSizeCopy}>
        <strong>
          We Recommend Size <span className={css.recommendedSize}>{predictedSize}</span>
        </strong>
        <button className={classNames} type="button" data-test-id={testId('initiateSizePrediction')} onClick={onOpenModal}>
          How do we know?
        </button>
      </div>
    );
  };

  const sizingPredictionCopy: Partial<OnDemandSizingCopy> = {
    ...onDemandSizingCopy,
    availablecopy: 'Which size fits me?',
    availableimage: undefined,
    selectedComponent: <RecommendedSizeCopy />,
    ignoredComponent: <RecommendedSizeCopy />
  };

  return (
    <div className={css.recommendedFitContainer}>
      <LinkStyleRecommendedSizing
        predictedSize={predictedSize}
        selectedSize={selectedSize}
        onOpenModal={onOpenModal}
        isDesktopView={isDesktopView}
        onDemandSizingCopy={isHydraSizePrediction ? sizingPredictionCopy : onDemandSizingCopy}
        isHydraSizePrediction={isHydraSizePrediction}
      />
      {isOnDemandSizingModalOpen && (
        <RecommendedSizeFinderModal
          productId={productId}
          gender={gender}
          onSetRecommendedFit={handleSetRecommendedFit}
          onClose={onCloseModal}
          location={location}
          handleCalculateSizeClick={handleCalculateSizeClick}
          isOpen={isOnDemandSizingModalOpen}
          predictedSize={predictedSize}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: AppState) => {
  const {
    headerFooter: {
      content: {
        Global: { slotData: { onDemandSizingCopy } } = {
          slotData: { onDemandSizingCopy: '' }
        }
      } = {}
    }
  } = state;
  return {
    predictedSize: selectPredictedSize(state),
    onDemandSizingCopy,
    isHydraSizePrediction: isAssigned(HYDRA_PRODUCT_SIZING_PREDICTION, 1, state)
  };
};

const connector = connect(mapStateToProps);

const ConnectedRecommendedSizing = connector(RecommendedSizing);

export default withErrorBoundary('ConnectedRecommendedSizing', ConnectedRecommendedSizing);
